import type {
  ComponentProps,
  ComponentType,
  FormHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from 'react'
import type { RadioButtonGroupProps } from '@/components/form/RadioButtonGroup'
import type { AdminRouterContext } from '@/lib/router-context'
import type {
  FsnNetworkIntegration,
  FsnNetworkIntegrationWithReadiness,
  FsnNetworkReadiness,
} from '@/lib/services/fsn-network/fsn-network.types'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import SaveBar from '@/components/forms/SaveBar'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  fsnNetworkQueries,
  useConnectFsnNetworkLoginMutation,
  useFsnNetworkIntegration,
  useSuspenseFsnNetworkIntegration,
  useUpdateFsnNetworkIntegrationMutation,
} from '@/lib/services/fsn-network/fsn-network.query'
import { connectFsnNetworkLoginSchema } from '@/lib/services/fsn-network/fsn-network.schema'
import { cn, getErrorMessage, readProperty } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { createFileRoute, notFound } from '@tanstack/react-router'
import {
  AlertTriangle,
  Check,
  CreditCard,
  Globe,
  Inbox,
  KeyRound,
  Loader2,
  Unplug,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

// The page merges saved settings, live diagnostics and its existing empty/legacy defaults.
// This view keeps those presentation states separate from the exact API resource.
export type FsnSettingsIntegration = Partial<
  Pick<
    FsnNetworkIntegration,
    | 'enabled'
    | 'connected'
    | 'has_token'
    | 'last_error'
    | 'pos_outside_area_enabled'
    | 'incoming_orders_enabled'
    | 'website_outside_area_enabled'
    | 'default_delivery_fee'
    | 'effective_default_delivery_fee'
    | 'website_outside_area_delivery_fee'
    | 'effective_website_outside_area_delivery_fee'
  >
> &
  Partial<Pick<FsnNetworkIntegrationWithReadiness, 'capabilities' | 'readiness'>> & {
    status_badge?:
      | FsnNetworkIntegration['status_badge']
      | FsnNetworkIntegrationWithReadiness['status_badge']
  }
type SetupIssue = NonNullable<FsnNetworkReadiness['issues'][keyof FsnNetworkReadiness['issues']]>
type SetupStatus = FsnNetworkReadiness['setup_status']
type SetupRow = SetupStatus['rows'][number]
type StatusBadge = { label: string | null; variant: ComponentProps<typeof Badge>['variant'] }
type FsnSettingsFormValues = {
  pos_outside_area_enabled: boolean
  incoming_orders_enabled: boolean
  website_outside_area_enabled: boolean
  default_delivery_fee: string
  website_outside_area_delivery_fee: string
}

const autofillOffFormProps = {
  autoComplete: 'off',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-protonpass-ignore': 'true',
} satisfies FormHTMLAttributes<HTMLFormElement> & Record<`data-${string}`, string>

const autofillOffInputProps = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'none',
  spellCheck: false,
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-protonpass-ignore': 'true',
  'data-form-type': 'other',
} satisfies InputHTMLAttributes<HTMLInputElement> & Record<`data-${string}`, string>

const fsnCredentialInputBaseProps = {
  ...autofillOffInputProps,
  autoComplete: 'one-time-code',
}

const FSN_SUPPORT_PHONE = '(800) 858-9925'

const networkUseOptions = [
  {
    id: 'false',
    name: 'No',
    start: (
      <span className="inline-flex items-center self-center" aria-hidden="true">
        <X className="size-3.5" />
      </span>
    ),
  },
  {
    id: 'true',
    name: 'Yes',
    start: (
      <span className="inline-flex items-center self-center" aria-hidden="true">
        <Check className="size-3.5" />
      </span>
    ),
  },
]

const networkUseRadioClassNames = {
  container: 'grid w-full min-w-0 grid-cols-2',
  button: 'min-w-0 items-center justify-center gap-1.5 px-4 text-sm leading-none',
  label: 'font-medium',
}

export async function fsnNetworkSettingsLoader({
  context,
  params,
}: {
  context: {
    ability: Pick<AdminRouterContext['ability'], 'can'>
    queryClient: AdminRouterContext['queryClient']
  }
  params: { orgId: string | number }
}): Promise<{ isReadOnly: false }> {
  if (
    !context.ability.can('list', 'business-settings') ||
    !context.ability.can('edit', 'fsn-network')
  ) {
    throw notFound()
  }

  await context.queryClient.ensureQueryData(
    fsnNetworkQueries.integration({ organizationId: params.orgId })
  )

  return { isReadOnly: false }
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/integrations/fsn-network/')({
  component: FsnNetworkSettingsPage,
  loader: fsnNetworkSettingsLoader,
})

function getDefaults(integration: FsnSettingsIntegration = {}): FsnSettingsFormValues {
  return {
    pos_outside_area_enabled: Boolean(integration.pos_outside_area_enabled ?? integration.enabled),
    incoming_orders_enabled: Boolean(integration.incoming_orders_enabled ?? integration.enabled),
    website_outside_area_enabled: Boolean(integration.website_outside_area_enabled),
    default_delivery_fee: integration.default_delivery_fee ?? '',
    website_outside_area_delivery_fee: integration.website_outside_area_delivery_fee ?? '',
  }
}

function validationErrorsForForm(error: unknown, knownFields: string[] = []) {
  const errors = readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')
  if (!errors) return undefined

  const entries: [string, unknown][] = Object.entries(errors)
  return entries.reduce<{ form: unknown[]; fields: Record<string, unknown[]> }>(
    (result, [key, messages]) => {
      const normalizedMessages: unknown[] = Array.isArray(messages) ? messages : [messages]

      if (knownFields.includes(key)) {
        return {
          ...result,
          fields: {
            ...result.fields,
            [key]: normalizedMessages,
          },
        }
      }

      return {
        ...result,
        form: [...result.form, ...normalizedMessages],
      }
    },
    { form: [], fields: {} }
  )
}

function tokenStatusBadge(integration: FsnSettingsIntegration = {}): StatusBadge {
  if (integration.status_badge?.label) {
    return {
      label: integration.status_badge.label,
      variant: integration.status_badge.variant,
    }
  }

  if (integration.connected) {
    return { label: 'Connected', variant: 'success' }
  }

  if (integration.has_token) {
    return { label: 'Needs attention', variant: 'destructive' }
  }

  return { label: 'Not connected', variant: 'outline' }
}

function ConnectionIssueNotice({ issue }: { issue?: SetupIssue | null }) {
  if (!issue) return null
  const isWarning = issue.variant === 'warning'

  return (
    <div
      className={cn(
        'rounded-lg border p-3 text-sm',
        isWarning
          ? 'border-status-warning-border bg-status-warning-background text-status-warning-foreground dark:border-status-warning-border dark:bg-status-warning-background dark:text-status-warning-foreground'
          : 'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground dark:border-status-destructive-border dark:bg-status-destructive-background dark:text-status-destructive-foreground'
      )}
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <p className="font-semibold">{issue.title}</p>
          {issue.detail ? (
            <p
              className={cn(
                isWarning
                  ? 'text-status-warning-foreground dark:text-status-warning-foreground'
                  : 'text-status-destructive-foreground dark:text-status-destructive-foreground'
              )}
            >
              {issue.detail}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function NetworkConnectionCard({
  connectionIssue = null,
  integration = {},
  isReadOnly = false,
  mutation,
  onDisconnect,
}: {
  connectionIssue?: SetupIssue | null
  integration?: FsnSettingsIntegration
  isReadOnly?: boolean
  mutation: { isPending: boolean }
  onDisconnect: () => Promise<void>
}) {
  const status = tokenStatusBadge(integration)

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-status-success-background text-status-success-foreground ring-1 ring-inset ring-status-success-border dark:bg-status-success-background dark:text-status-success-foreground dark:ring-status-success-border">
              <FsnNetworkIcon className="size-5" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-xl">Flower Shop Network account</CardTitle>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>
            </div>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghostMuted"
                className="w-full sm:w-auto"
                disabled={isReadOnly || mutation.isPending}
              >
                <Unplug className="size-4" />
                Disconnect
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disconnect Flower Shop Network?</AlertDialogTitle>
                <AlertDialogDescription>
                  Incoming F2F order checks and out-of-area delivery settings will turn off until
                  you connect again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDisconnect}>Disconnect</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <ConnectionIssueNotice issue={connectionIssue} />
      </CardContent>
    </Card>
  )
}

function NetworkSettingWarning({ issue }: { issue?: SetupIssue | null }) {
  if (!issue) return null

  return (
    <div className="rounded-lg border border-status-destructive-border bg-status-destructive-background p-3 text-sm text-status-destructive-foreground dark:border-status-destructive-border dark:bg-status-destructive-background dark:text-status-destructive-foreground">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 space-y-1">
          <p className="font-semibold">{issue.title}</p>
          {issue.detail ? (
            <p className="text-status-destructive-foreground dark:text-status-destructive-foreground">
              {issue.detail}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function fsnStatusRowState(status: SetupRow['status']) {
  if (status === true) {
    return {
      icon: Check,
      iconClassName:
        'bg-status-success-background text-status-success-foreground ring-status-success-border',
    }
  }

  if (status === false) {
    return {
      icon: X,
      iconClassName:
        'bg-status-destructive-background text-status-destructive-foreground ring-status-destructive-border',
    }
  }

  if (status === 'warning') {
    return {
      icon: AlertTriangle,
      iconClassName:
        'bg-status-warning-background text-status-warning-foreground ring-status-warning-border',
    }
  }

  return {
    icon: AlertTriangle,
    iconClassName: 'bg-muted text-muted-foreground ring-border',
  }
}

function FsnStatusRow({ status, text }: Pick<SetupRow, 'status' | 'text'>) {
  const { icon: Icon, iconClassName } = fsnStatusRowState(status)

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background/45 px-3 py-2.5">
      <span
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-full ring-1 ring-inset',
          iconClassName
        )}
        aria-hidden="true"
      >
        <Icon className="size-3.5" />
      </span>
      <p className="min-w-0 text-sm leading-5 text-foreground">{text}</p>
    </div>
  )
}

function FsnSetupStatusAction({ action }: { action?: SetupStatus['action'] }) {
  if (!action?.message) return null

  return (
    <Typography
      variant="supporting"
      className="block rounded-lg border border-border/60 bg-muted/20 p-3"
    >
      {action.message}
    </Typography>
  )
}

function FsnSetupStatusCard({
  checkFailed = false,
  hasLocalMismatch,
  integration,
  isChecking = false,
}: {
  checkFailed?: boolean
  hasLocalMismatch: boolean
  integration: FsnSettingsIntegration
  isChecking?: boolean
}) {
  const setupStatus = integration?.readiness?.setup_status
  const hasSetupStatus =
    !checkFailed && setupStatus && integration?.readiness?.status !== 'not_connected'

  const badge: StatusBadge | undefined = checkFailed
    ? { label: 'Could not verify', variant: 'muted' }
    : hasSetupStatus && hasLocalMismatch && setupStatus.mismatch_badge
      ? setupStatus.mismatch_badge
      : setupStatus?.badge
  const action =
    hasSetupStatus && hasLocalMismatch && setupStatus.mismatch_action
      ? setupStatus.mismatch_action
      : hasSetupStatus
        ? setupStatus.action
        : null

  return (
    <Card aria-label="FSN setup status">
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-1">
            <CardTitle>FSN setup status</CardTitle>
            <CardDescription>
              This shows how Flower Shop Network has this account configured.
            </CardDescription>
          </div>
          {badge ? (
            <Badge variant={badge.variant}>{badge.label}</Badge>
          ) : isChecking ? (
            <Badge variant="outline">
              <Loader2 className="size-3.5 animate-spin" />
              Checking…
            </Badge>
          ) : null}
        </div>
        {hasSetupStatus && setupStatus.account_label ? (
          <Badge variant="outline" className="w-fit">
            {setupStatus.account_label}
          </Badge>
        ) : null}
      </CardHeader>
      {hasSetupStatus ? (
        <CardContent className="space-y-3">
          {(setupStatus.rows ?? []).map((row) => (
            <FsnStatusRow key={row.key} status={row.status} text={row.text} />
          ))}
          <FsnSetupStatusAction action={action} />
        </CardContent>
      ) : checkFailed ? (
        <CardContent>
          <Typography variant="supporting">
            Could not check FSN. You can still change these settings.
          </Typography>
        </CardContent>
      ) : null}
    </Card>
  )
}

function NetworkSettingCardHeader({
  icon: Icon,
  isEnabled,
  title,
}: {
  icon: typeof Check
  isEnabled: boolean
  title: ReactNode
}) {
  return (
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-status-info-background text-status-info-foreground ring-1 ring-inset ring-status-info-border">
            <Icon className="size-5" aria-hidden="true" />
          </div>
          <CardTitle>{title}</CardTitle>
        </div>
        <Badge variant={isEnabled ? 'info' : 'muted'}>{isEnabled ? 'On' : 'Off'}</Badge>
      </div>
    </CardHeader>
  )
}

function NetworkUseSettingField({
  ariaLabel,
  description,
  disabled = false,
  displayValue,
  field,
  title,
}: {
  ariaLabel: string
  description?: ReactNode
  disabled?: boolean
  displayValue: boolean
  field: { RadioButtonGroup: ComponentType<RadioButtonGroupProps> }
  title: ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border/60 bg-muted/20 p-3 transition-colors sm:p-4',
        displayValue ? 'border-status-info-border bg-status-info-background' : 'bg-muted/15'
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <Typography variant="labelStrong" className="block">
            {title}
          </Typography>
          {description ? (
            <Typography variant="supporting" className="mt-1 block">
              {description}
            </Typography>
          ) : null}
        </div>
        <field.RadioButtonGroup
          appearance="segmented"
          aria-label={ariaLabel}
          className="w-full shrink-0 sm:w-auto"
          classNames={networkUseRadioClassNames}
          disabled={disabled}
          displayValue={displayValue}
          joined
          options={networkUseOptions}
          parseValue={(value) => value === 'true'}
          size="sm"
          unselectedVariant="outline"
        />
      </div>
    </div>
  )
}

export function FsnNetworkSettingsPage() {
  const orgId = useRouteOrganizationIdParam()
  const { isReadOnly } = useIsReadOnly()
  const { data: savedIntegration = {} }: { data?: FsnSettingsIntegration } =
    useSuspenseFsnNetworkIntegration({
      organizationId: orgId,
    })
  const readinessQuery: { data?: FsnSettingsIntegration; isError: boolean; isFetching: boolean } =
    useFsnNetworkIntegration({
      organizationId: orgId,
      includeReadiness: true,
      enabled: Boolean(savedIntegration.has_token),
      retry: false,
      staleTime: 60_000,
    })
  // Keep form defaults stable while saved settings remain authoritative over live diagnostics.
  const integration: FsnSettingsIntegration = useMemo(
    () =>
      savedIntegration.has_token && readinessQuery.data
        ? {
            ...readinessQuery.data,
            ...savedIntegration,
            capabilities: readinessQuery.data.capabilities,
            readiness: readinessQuery.data.readiness,
            status_badge: readinessQuery.data.status_badge ?? savedIntegration.status_badge,
          }
        : savedIntegration,
    [readinessQuery.data, savedIntegration]
  )
  const updateMutation = useUpdateFsnNetworkIntegrationMutation()
  const connectLoginMutation = useConnectFsnNetworkLoginMutation()
  const [fsnCredentialsUnlocked, setFsnCredentialsUnlocked] = useState(false)
  const defaultValues = useMemo(() => getDefaults(integration), [integration])
  const isConnected = Boolean(integration.connected)
  const connectionNeedsAttention = Boolean(integration.has_token && !isConnected)
  const unlockFsnCredentials = () => setFsnCredentialsUnlocked(true)
  const fsnCredentialInputProps = {
    ...fsnCredentialInputBaseProps,
    readOnly: !fsnCredentialsUnlocked,
    onFocus: unlockFsnCredentials,
    onPointerDown: unlockFsnCredentials,
  }

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return undefined

        try {
          const payload = {
            pos_outside_area_enabled: isConnected && Boolean(value.pos_outside_area_enabled),
            incoming_orders_enabled: isConnected && Boolean(value.incoming_orders_enabled),
            website_outside_area_enabled:
              isConnected && Boolean(value.website_outside_area_enabled),
            default_delivery_fee:
              String(value.default_delivery_fee ?? '').trim() === ''
                ? null
                : value.default_delivery_fee,
            website_outside_area_delivery_fee:
              String(value.website_outside_area_delivery_fee ?? '').trim() === ''
                ? null
                : value.website_outside_area_delivery_fee,
          }

          const saved = await updateMutation.mutateAsync(payload)
          toast.success('Flower Shop Network settings saved')
          setTimeout(() => {
            formApi.reset(getDefaults(saved))
          })
        } catch (error) {
          if (readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')) {
            return validationErrorsForForm(error, [
              'pos_outside_area_enabled',
              'incoming_orders_enabled',
              'website_outside_area_enabled',
              'default_delivery_fee',
              'website_outside_area_delivery_fee',
            ])
          }

          toast.error('Could not save Flower Shop Network settings', {
            description: getErrorMessage(error),
          })
        }
        return undefined
      },
    },
  })

  const connectForm = useAppForm({
    defaultValues: {
      username: '',
      password: '',
    },
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return undefined

        try {
          const connected = await connectLoginMutation.mutateAsync({
            username: value.username.trim(),
            password: value.password,
          })

          toast.success('Flower Shop Network connected')
          setTimeout(() => {
            formApi.reset()
            form.reset(getDefaults(connected))
          })
        } catch (error) {
          if (readProperty(readProperty(readProperty(error, 'response'), 'data'), 'errors')) {
            return validationErrorsForForm(error, ['username', 'password'])
          }

          toast.error('Could not connect Flower Shop Network', {
            description: getErrorMessage(error),
          })
        }
        return undefined
      },
    },
  })

  const posOutsideAreaEnabled = useStore(
    form.store,
    (state) => state.values.pos_outside_area_enabled === true
  )
  const incomingOrdersEnabled = useStore(
    form.store,
    (state) => state.values.incoming_orders_enabled === true
  )
  const websiteOutsideAreaEnabled = useStore(
    form.store,
    (state) => state.values.website_outside_area_enabled === true
  )
  const readinessIssues: Partial<FsnNetworkReadiness['issues']> = readinessQuery.isError
    ? {}
    : (integration.readiness?.issues ?? {})
  const connectionIssue = readinessIssues.connection ?? null
  const incomingCapabilityIssue = incomingOrdersEnabled
    ? (readinessIssues.incoming_orders ?? null)
    : null
  const outgoingCapabilityIssue =
    posOutsideAreaEnabled || websiteOutsideAreaEnabled
      ? (readinessIssues.outgoing_orders ?? null)
      : null
  const hasLocalMismatch = Boolean(incomingCapabilityIssue || outgoingCapabilityIssue)

  async function handleDisconnect() {
    try {
      const saved = await updateMutation.mutateAsync({
        pos_outside_area_enabled: false,
        incoming_orders_enabled: false,
        website_outside_area_enabled: false,
        default_delivery_fee: integration.default_delivery_fee ?? null,
        website_outside_area_delivery_fee: integration.website_outside_area_delivery_fee ?? null,
        clear_token: true,
      })
      toast.success('Flower Shop Network disconnected')
      form.reset(getDefaults(saved))
    } catch (error) {
      toast.error('Could not disconnect Flower Shop Network', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="max-w-6xl space-y-5">
      <AdminPageHeader
        icon={FsnNetworkIcon}
        title="Flower Shop Network"
        description="Manage incoming F2F orders and out-of-area delivery through Flower Shop Network."
      />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice>
            Switch to the account that owns the Flower Shop Network connection.
          </OrganizationContextNotice>
        }
      >
        <>
          <Typography variant="supporting" className="block">
            Need to join Flower Shop Network or get help connecting? Call {FSN_SUPPORT_PHONE}.
          </Typography>

          {connectionNeedsAttention && integration.last_error ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Flower Shop Network connection needs attention</AlertTitle>
              <AlertDescription>{integration.last_error}</AlertDescription>
            </Alert>
          ) : null}

          {isConnected ? (
            <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(26rem,1fr)] xl:items-start">
              <div className="space-y-5">
                <NetworkConnectionCard
                  connectionIssue={connectionIssue}
                  integration={integration}
                  isReadOnly={isReadOnly}
                  mutation={updateMutation}
                  onDisconnect={handleDisconnect}
                />

                <form.AppForm>
                  <form.Form {...autofillOffFormProps} className="space-y-5">
                    <Card>
                      <NetworkSettingCardHeader
                        icon={Inbox}
                        isEnabled={incomingOrdersEnabled}
                        title="Incoming F2F orders"
                      />
                      <CardContent className="space-y-5">
                        <form.FormError title="Unable to save Flower Shop Network settings" />

                        <form.AppField name="incoming_orders_enabled">
                          {(field) => (
                            <NetworkUseSettingField
                              ariaLabel="Check for incoming F2F orders"
                              title="Check Flower Shop Network for incoming F2F orders?"
                              description="Our system will look for wire-in orders sent to this shop."
                              field={field}
                              disabled={isReadOnly}
                              displayValue={incomingOrdersEnabled}
                            />
                          )}
                        </form.AppField>

                        <NetworkSettingWarning issue={incomingCapabilityIssue} />

                        {!incomingOrdersEnabled ? (
                          <Typography
                            variant="supporting"
                            className="block rounded-lg border border-border/60 bg-muted/20 p-3"
                          >
                            Our system will not check Flower Shop Network for incoming F2F orders.
                          </Typography>
                        ) : null}
                      </CardContent>
                    </Card>

                    <Card>
                      <NetworkSettingCardHeader
                        icon={CreditCard}
                        isEnabled={posOutsideAreaEnabled}
                        title="POS out-of-area delivery"
                      />
                      <CardContent className="space-y-5">
                        <form.AppField name="pos_outside_area_enabled">
                          {(field) => (
                            <NetworkUseSettingField
                              ariaLabel="Allow out-of-delivery-area POS orders"
                              title={
                                <>
                                  Allow out-of-delivery-area <strong>POS</strong> orders?
                                </>
                              }
                              description="Staff can save out-of-area addresses on new and existing orders, including website orders, and send them to Flower Shop Network."
                              field={field}
                              disabled={isReadOnly}
                              displayValue={posOutsideAreaEnabled}
                            />
                          )}
                        </form.AppField>

                        <NetworkSettingWarning
                          issue={posOutsideAreaEnabled ? outgoingCapabilityIssue : null}
                        />

                        {posOutsideAreaEnabled ? (
                          <div className="max-w-xs">
                            <form.AppField name="default_delivery_fee">
                              {(field) => (
                                <field.MoneyInput
                                  {...autofillOffInputProps}
                                  id="fsn-network-outside-area-fee-entry"
                                  name="fsn_network_outside_area_fee_entry"
                                  label="POS out-of-area delivery fee"
                                  placeholder={
                                    integration.effective_default_delivery_fee
                                      ? `${integration.effective_default_delivery_fee}`
                                      : '15.00'
                                  }
                                  showFreeBadge={false}
                                  formatEmptyAsZero={false}
                                  disabled={isReadOnly}
                                />
                              )}
                            </form.AppField>
                          </div>
                        ) : (
                          <Typography
                            variant="supporting"
                            className="block rounded-lg border border-border/60 bg-muted/20 p-3"
                          >
                            POS and order edits will keep blocking out-of-area delivery addresses.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <NetworkSettingCardHeader
                        icon={Globe}
                        isEnabled={websiteOutsideAreaEnabled}
                        title="Website out-of-area delivery"
                      />
                      <CardContent className="space-y-5">
                        <form.AppField name="website_outside_area_enabled">
                          {(field) => (
                            <NetworkUseSettingField
                              ariaLabel="Allow out-of-delivery-area Website orders"
                              title={
                                <>
                                  Allow out-of-delivery-area <strong>Website</strong> orders?
                                </>
                              }
                              description="Shoppers can check out, but staff must still send each order to Flower Shop Network."
                              field={field}
                              disabled={isReadOnly}
                              displayValue={websiteOutsideAreaEnabled}
                            />
                          )}
                        </form.AppField>

                        <NetworkSettingWarning
                          issue={websiteOutsideAreaEnabled ? outgoingCapabilityIssue : null}
                        />

                        {websiteOutsideAreaEnabled ? (
                          <div className="max-w-xs">
                            <form.AppField name="website_outside_area_delivery_fee">
                              {(field) => (
                                <field.MoneyInput
                                  {...autofillOffInputProps}
                                  id="fsn-network-website-outside-area-fee-entry"
                                  name="fsn_network_website_outside_area_fee_entry"
                                  label="Website out-of-area delivery fee"
                                  placeholder={
                                    integration.effective_website_outside_area_delivery_fee
                                      ? `${integration.effective_website_outside_area_delivery_fee}`
                                      : '15.00'
                                  }
                                  showFreeBadge={false}
                                  formatEmptyAsZero={false}
                                  disabled={isReadOnly}
                                />
                              )}
                            </form.AppField>
                          </div>
                        ) : (
                          <Typography
                            variant="supporting"
                            className="block rounded-lg border border-border/60 bg-muted/20 p-3"
                          >
                            Website checkout will keep blocking out-of-area delivery addresses.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>

                    <Alert variant="warning">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Flower Shop Network account charges</AlertTitle>
                      <AlertDescription>
                        If you send an out-of-area order to Flower Shop Network, Flower Shop Network
                        will charge your FSN account.
                      </AlertDescription>
                    </Alert>

                    {!isReadOnly ? (
                      <SaveBar mutation={updateMutation} submitButtonText="Save Settings" />
                    ) : null}
                  </form.Form>
                </form.AppForm>
              </div>

              <aside className="xl:sticky xl:top-4">
                <FsnSetupStatusCard
                  checkFailed={readinessQuery.isError}
                  hasLocalMismatch={hasLocalMismatch}
                  integration={integration}
                  isChecking={readinessQuery.isFetching}
                />
              </aside>
            </div>
          ) : (
            <connectForm.AppForm>
              <connectForm.Form {...autofillOffFormProps}>
                <Card>
                  <CardHeader>
                    <CardTitle>Connect with FSN login</CardTitle>
                    <CardDescription>
                      Flower Shop Network must turn on API access before you connect your account.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <connectForm.FormError title="Unable to connect Flower Shop Network" />

                    <div className="grid gap-4 md:grid-cols-2">
                      <connectForm.AppField
                        name="username"
                        validators={{ onChange: connectFsnNetworkLoginSchema.shape.username }}
                      >
                        {(field) => (
                          <field.TextInput
                            {...fsnCredentialInputProps}
                            id="fsn-network-shop-code-entry"
                            name="fsn_network_shop_code_entry"
                            label="FSN shop ID"
                            placeholder="Enter your FSN shop ID"
                            disabled={isReadOnly}
                          />
                        )}
                      </connectForm.AppField>

                      <connectForm.AppField
                        name="password"
                        validators={{ onChange: connectFsnNetworkLoginSchema.shape.password }}
                      >
                        {(field) => (
                          <field.TextInput
                            {...fsnCredentialInputProps}
                            id="fsn-network-access-phrase-entry"
                            name="fsn_network_access_phrase_entry"
                            type="password"
                            label="FSN password"
                            placeholder="Enter your FSN password"
                            disabled={isReadOnly}
                          />
                        )}
                      </connectForm.AppField>
                    </div>

                    <div className="flex justify-end">
                      <connectForm.SubmitButton
                        mutation={connectLoginMutation}
                        className="w-full md:w-auto"
                        keepChildrenWhileLoading
                      >
                        <KeyRound className="size-4" />
                        Connect Account
                      </connectForm.SubmitButton>
                    </div>
                  </CardContent>
                </Card>
              </connectForm.Form>
            </connectForm.AppForm>
          )}
        </>
      </IfGlobalOrg>
    </div>
  )
}
